import { useState, type FormEvent } from 'react';
import { DraftNotice } from '../../components/DraftNotice';
import { Modal } from '../../components/Modal';
import { planErrorMessage } from '../../components/plan/planErrors';
import { TicketHead } from '../../components/TicketHead';
import { clearDraft, knowledgeBaseDraftKey, restoreDraft } from '../../lib/draftStorage';
import { useDraft } from '../../lib/useDraft';
import type { ProofPointCreate, ProofPointRead } from '../../types/api';
import { proofPointsApi } from './knowledgeBaseApi';
import { SectionShell } from './SectionShell';
import { useCollection } from './useCollection';
import { useSectionCapacity } from './useSectionCapacity';
import { useBrandCopy } from '../onboarding/useBrandCopy';

/** Autosaved shape. Field names are the form's, not the API's. */
interface ProofPointDraft {
  claim: string;
  evidence: string;
  sourceUrl: string;
}

function proofPointDraftKey(businessId: string, item: ProofPointRead | null): string {
  return knowledgeBaseDraftKey('proof-points', businessId, item?.id ?? null);
}

interface ProofPointFormProps {
  businessId: string;
  initial: ProofPointRead | null;
  isSaving: boolean;
  error: string | null;
  onSubmit: (body: ProofPointCreate) => void;
  onCancel: () => void;
}

function ProofPointForm({
  businessId,
  initial,
  isSaving,
  error,
  onSubmit,
  onCancel,
}: ProofPointFormProps) {
  const draftKey = proofPointDraftKey(businessId, initial);
  const pristine: ProofPointDraft = {
    claim: initial?.claim ?? '',
    evidence: initial?.evidence ?? '',
    sourceUrl: initial?.source_url ?? '',
  };
  // The form mounts when the modal opens, so this initialiser is the "on open"
  // hook — no effect, no second render.
  const [restored] = useState(() => restoreDraft(draftKey, pristine));
  const [claim, setClaim] = useState(restored.value.claim);
  const [evidence, setEvidence] = useState(restored.value.evidence);
  const [sourceUrl, setSourceUrl] = useState(restored.value.sourceUrl);
  const [restoredAt, setRestoredAt] = useState<string | null>(restored.savedAt);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useDraft(draftKey, { claim, evidence, sourceUrl }, pristine);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedUrl = sourceUrl.trim();
    if (!claim.trim()) {
      setFieldError('State the claim before saving.');
      return;
    }
    if (trimmedUrl && !/^https?:\/\/\S+$/i.test(trimmedUrl)) {
      setFieldError('Source URL must start with http:// or https://.');
      return;
    }
    setFieldError(null);
    onSubmit({
      claim: claim.trim(),
      evidence: evidence.trim() || null,
      source_url: trimmedUrl || null,
    });
  }

  /**
   * Cancel is a decision to throw the work away. Escape and the header Close
   * are not — those leave the draft where it is.
   */
  function handleCancel() {
    clearDraft(draftKey);
    onCancel();
  }

  const message = fieldError ?? error;

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      {restoredAt ? (
        <DraftNotice savedAt={restoredAt} onDismiss={() => setRestoredAt(null)} />
      ) : null}

      <p className="kb-form__hint">
        Only things that are true and checkable — a review, a number, an award.
      </p>

      <label className="field">
        <span className="field__label">Claim</span>
        <input
          className="field__input"
          type="text"
          maxLength={300}
          placeholder="Rated 4.9 by 380 customers"
          value={claim}
          onChange={(event) => setClaim(event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Evidence</span>
        <textarea
          className="field__input"
          rows={4}
          placeholder="Where the number comes from, and when it was measured."
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
        />
        <span className="field__hint">We read this when we check the claim.</span>
      </label>

      <label className="field">
        <span className="field__label">Source URL</span>
        <input
          className="field__input"
          type="url"
          placeholder="https://"
          value={sourceUrl}
          onChange={(event) => setSourceUrl(event.target.value)}
        />
      </label>

      {message ? (
        <p className="form__error" role="alert">
          {message}
        </p>
      ) : null}

      <div className="modal__actions">
        <button className="button" type="button" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </button>
        <button className="button button--primary" type="submit" disabled={isSaving}>
          {isSaving ? 'Saving' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/**
 * Proof points — the evidence behind every claim the copy is allowed to make.
 * This is what the fact-check agent checks captions against.
 */
export function ProofPointsSection({ businessId }: { businessId: string }) {
  const collection = useCollection(proofPointsApi, businessId, 'proof_points');
  const { capacity, isFull, planName, fullTitle, refreshCapacity } = useSectionCapacity(
    businessId,
    'testimonials',
  );
  const copy = useBrandCopy();
  const [editing, setEditing] = useState<ProofPointRead | null | 'new'>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  function startCreate() {
    setWriteError(null);
    setEditing('new');
  }

  async function handleSubmit(body: ProofPointCreate) {
    const draftKey = proofPointDraftKey(businessId, editing === 'new' ? null : editing);
    setIsSaving(true);
    setWriteError(null);
    try {
      if (editing && editing !== 'new') {
        await collection.update(editing.id, body);
      } else {
        await collection.create(body);
        refreshCapacity();
      }
      // The record is on file; the local copy has nothing left to protect.
      clearDraft(draftKey);
      setEditing(null);
    } catch (caught) {
      // A 402 at the cap: the counts were stale, so re-read them too.
      refreshCapacity();
      setWriteError(
        planErrorMessage(caught, 'That testimonial or result did not save.', {
          plan: planName,
          subject: copy.sections['proof-points'],
        }),
      );
    } finally {
      setIsSaving(false);
    }
  }

  /** Optimistic: the row goes now, the DELETE waits out the undo window. */
  function startDelete(item: ProofPointRead) {
    collection.removeWithUndo({
      id: item.id,
      label: item.claim,
      failureMessage: 'That testimonial or result could not be deleted.',
      onRemoved: refreshCapacity,
    });
  }

  return (
    <>
      <SectionShell
        title={copy.sections['proof-points']}
        lede={copy.records.proofPoints.lede}
        action={
          <button
            className="button button--primary"
            type="button"
            onClick={startCreate}
            disabled={isFull}
            title={fullTitle}
          >
            {copy.records.proofPoints.add}
          </button>
        }
        capacity={capacity}
        isLoading={collection.isLoading}
        error={collection.error ?? collection.deleteError}
        isEmpty={collection.items.length === 0}
        emptyMessage={copy.records.proofPoints.empty}
        emptyAction={
          <button
            className="button"
            type="button"
            onClick={startCreate}
            disabled={isFull}
            title={fullTitle}
          >
            {copy.records.proofPoints.add}
          </button>
        }
      >
        <ul className="record-list">
          {collection.items.map((item) => (
            <li className="record" key={item.id}>
              <TicketHead label={copy.records.proofPoints.singular} timestamp={item.created_at} />
              <div className="record__head">
                <span className="record__title">{item.claim}</span>
              </div>
              {item.evidence ? <p className="record__body">{item.evidence}</p> : null}
              {item.source_url ? (
                <a
                  className="record__link"
                  href={item.source_url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {item.source_url}
                </a>
              ) : null}
              <div className="record__actions">
                <button
                  className="button button--ghost"
                  type="button"
                  onClick={() => {
                    setWriteError(null);
                    setEditing(item);
                  }}
                >
                  Edit
                </button>
                <button
                  className="button button--ghost button--danger-text"
                  type="button"
                  onClick={() => startDelete(item)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </SectionShell>

      {editing ? (
        <Modal
          title={
            editing === 'new'
              ? `New ${copy.records.proofPoints.singular}`
              : `Edit ${copy.records.proofPoints.singular}`
          }
          kind={editing === 'new' ? 'new testimonial' : 'testimonial'}
          onDismiss={() => setEditing(null)}
        >
          <ProofPointForm
            businessId={businessId}
            initial={editing === 'new' ? null : editing}
            isSaving={isSaving}
            error={writeError}
            onSubmit={(body) => void handleSubmit(body)}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      ) : null}
    </>
  );
}
